package org.zq.astrock_ai.ui.research.gushi.components

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import kotlinx.coroutines.delay
import org.zq.astrock_ai.data.StockProfile
import org.zq.astrock_ai.network.EastMoneyStockSearchClient
import org.zq.astrock_ai.ui.research.gushi.StockColors
import org.zq.astrock_ai.ui.research.gushi.adaptiveDialogWidth

@Composable
fun AddStockToWatchlistDialog(
    candidates: List<StockProfile>,
    existingCodes: Set<String>,
    loading: Boolean,
    onDismiss: () -> Unit,
    onAdd: (StockProfile) -> Unit,
) {
    var search by remember { mutableStateOf("") }
    var remoteResults by remember { mutableStateOf<List<StockProfile>>(emptyList()) }
    var remoteLoading by remember { mutableStateOf(false) }
    var remoteFailed by remember { mutableStateOf(false) }
    val searchClient = remember { EastMoneyStockSearchClient() }
    val localMatches = remember(candidates, search) {
        val query = search.trim()
        if (query.isBlank()) {
            candidates
        } else {
            candidates.filter {
                it.name.contains(query, ignoreCase = true) ||
                    it.code.contains(query) ||
                    it.fullCode.contains(query, ignoreCase = true)
            }
        }
    }
    val visible = if (search.isBlank()) {
        candidates
    } else {
        (remoteResults + localMatches)
            .distinctBy { it.code }
            .filter { it.code !in existingCodes }
    }

    LaunchedEffect(search, existingCodes) {
        val query = search.trim()
        remoteResults = emptyList()
        remoteFailed = false
        if (query.isBlank()) return@LaunchedEffect
        delay(300)
        remoteLoading = true
        runCatching { searchClient.search(query) }
            .onSuccess { results ->
                remoteResults = results.filter { it.code !in existingCodes }
            }
            .onFailure { remoteFailed = true }
        remoteLoading = false
    }
    val shape = RoundedCornerShape(14.dp)

    Dialog(onDismissRequest = onDismiss) {
        Column(
            modifier = Modifier
                .adaptiveDialogWidth(420.dp)
                .background(Color.White, shape)
                .border(BorderStroke(1.dp, StockColors.Border), shape)
                .padding(20.dp),
        ) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Column(Modifier.weight(1f)) {
                    Text(
                        "添加自选股",
                        color = StockColors.TextPrimary,
                        fontWeight = FontWeight.Bold,
                        fontSize = 18.sp,
                    )
                    Text(
                        "搜索并点击股票即可加入",
                        color = StockColors.TextSecondary,
                        fontSize = 12.sp,
                    )
                }
                Text(
                    "关闭",
                    color = StockColors.TextSecondary,
                    fontSize = 13.sp,
                    modifier = Modifier
                        .clickable(onClick = onDismiss)
                        .padding(8.dp),
                )
            }

            Spacer(Modifier.height(14.dp))
            SearchBox(
                value = search,
                onValueChange = { search = it },
                placeholder = "搜索股票名称或代码",
                modifier = Modifier.fillMaxWidth(),
            )
            Spacer(Modifier.height(12.dp))

            if (loading || remoteLoading) {
                Box(
                    modifier = Modifier.fillMaxWidth().height(250.dp),
                    contentAlignment = Alignment.Center,
                ) {
                    Text(
                        if (remoteLoading) "正在搜索全市场股票…" else "正在加载可选股票…",
                        color = StockColors.TextSecondary,
                        fontSize = 13.sp,
                    )
                }
            } else if (visible.isEmpty()) {
                Box(
                    modifier = Modifier.fillMaxWidth().height(250.dp),
                    contentAlignment = Alignment.Center,
                ) {
                    Text(
                        when {
                            remoteFailed -> "搜索服务暂时不可用，请稍后重试"
                            search.isNotBlank() -> "未找到A股股票，请检查名称或代码"
                            else -> "暂无可添加股票"
                        },
                        color = StockColors.TextSecondary,
                        fontSize = 13.sp,
                    )
                }
            } else {
                LazyColumn(
                    modifier = Modifier.fillMaxWidth().height(300.dp),
                    verticalArrangement = Arrangement.spacedBy(6.dp),
                ) {
                    items(visible, key = { it.code }) { stock ->
                        Row(
                            modifier = Modifier
                                .fillMaxWidth()
                                .background(Color(0xFFF8F9FB), RoundedCornerShape(9.dp))
                                .clickable { onAdd(stock) }
                                .padding(horizontal = 12.dp, vertical = 10.dp),
                            verticalAlignment = Alignment.CenterVertically,
                        ) {
                            Box(
                                modifier = Modifier
                                    .size(34.dp)
                                    .background(StockColors.UpSoft, RoundedCornerShape(8.dp)),
                                contentAlignment = Alignment.Center,
                            ) {
                                Text(
                                    stock.initial,
                                    color = StockColors.UpRed,
                                    fontWeight = FontWeight.Bold,
                                    fontSize = 13.sp,
                                )
                            }
                            Spacer(Modifier.width(10.dp))
                            Column(Modifier.weight(1f)) {
                                Text(
                                    stock.name,
                                    color = StockColors.TextPrimary,
                                    fontWeight = FontWeight.Medium,
                                    fontSize = 14.sp,
                                )
                                Text(
                                    stock.fullCode,
                                    color = StockColors.TextSecondary,
                                    fontSize = 11.sp,
                                )
                            }
                            Column(horizontalAlignment = Alignment.End) {
                                Text(stock.price, color = StockColors.TextPrimary, fontSize = 13.sp)
                                Text(
                                    stock.changePercent,
                                    color = if (stock.isUp) StockColors.UpRed else StockColors.DownGreen,
                                    fontSize = 12.sp,
                                )
                            }
                            Spacer(Modifier.width(12.dp))
                            Text("+ 添加", color = StockColors.PrimaryBlue, fontSize = 12.sp)
                        }
                    }
                }
            }
        }
    }
}
